import { useState } from "react";
import { PdfIcon, ImageIcon, EditIcon, DragAndDropIcon } from "./Icons";
import {
  EyeIcon,
  Trash2,
  ArrowDownToLine
} from "lucide-react";
import { Modal } from "antd";
import { Button } from "./Button";
import {
  DragAndDropTitle,
  DragAndDropUpload,
  DragAndDropHelpText,
} from "./DragAndUpload";
import apiService from "../../../../services/apiService";
import { toast } from "react-toastify";

interface Document {
  name: string;
  sizeInBytes: number;
  fileType: string;
  documentType: string;
  documentUrl: string;
  documentCategory: string;
  status: string;
  signedUrl?: string;
  imageUrl?: string;
  s3FileKey?: string;
}

interface UploadPreviewProps {
  document: Document;
  onDelete?:()=>void
}

interface InlinePreviewProps {
  document: Document;
  onDelete?:()=>void
}

interface InlinePreviewListProps {
  documentList: Document[];
  onDelete?:()=>void
}

interface UploadMultipleFilesProps {
  onDrop: (data: { acceptedFiles: File[]; documentType: string }) => void;
  documents: Document[];
  documentType: string;
  onDelete?:()=>void
}

const getFileType = (fileName: string): string | undefined => {
  // Get file extension
  const fileType = fileName.split(".").pop();
  return fileType;
};

const bytesToMB = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(2);

export const UploadPreview = ({ document, onDelete }: UploadPreviewProps) => {
  const [showPreviewModal, toggleShowPreviewModal] = useState(false);

  if (!document) return null;
  const { signedUrl, imageUrl, name, s3FileKey } = document;
  const url = signedUrl || imageUrl;
  if (!url) return null;
  const extension = getFileType(name);

  const isPdf = extension === "pdf";

  return (
    <div className="flex flex-col h-full justify-end items-center w-full">
      {isPdf ? (
        <div className="flex justify-between items-center grow">
          <iframe src={url} width={"100%"} />
        </div>
      ) : (
        <img
          src={url}
          className="h-[100px] w-[200px] mb-8 object-contain"
          alt={name}
        />
      )}
      <div className="flex flex-row gap-8 px-2 relative w-full">
        <div className="text-sm font-medium text-left mb-2">
          <div> {document.name}</div>
          <span className="text-[#6B7280] text-xs">
            {bytesToMB(document.sizeInBytes)} MB
          </span>
        </div>
        <div className="flex flex-row items-end absolute bottom-0 right-0">
          <Button variant={"ghost"} variantColor={"primary"} size={"icon-sm"}>
            <EditIcon />
          </Button>
          <Button
            variant={"ghost"}
            variantColor={"secondary"}
            size={"icon-sm"}
            onClick={(e) => {
              e.stopPropagation();
              toggleShowPreviewModal((prev) => !prev);
            }}
          >
            <EyeIcon />
          </Button>
          <Button
            variant={"ghost"}
            variantColor={"destructive"}
            size={"icon-sm"}
            onClick={(e) => {
              e.stopPropagation();
              apiService
                .delete(`/customers/document/`, {
                  s3FileKey: s3FileKey,documentCategory: document.documentCategory,
                })
                .then((response) => {
                  // Store the API response in a variable
                  const apiResponse = response;

                  // Show success toast
                  toast.info(
                    (apiResponse as any).message ||
                      "Document deleted successfully"
                  );

                  // Call the onDelete callback with the response if needed
                  onDelete();
                })
                .catch((error) => {
                  // Show error toast
                  toast.error(error.message || "Unknown error");

                  // Log the error for debugging
                  console.error("Document deletion error:", error);
                });
            }}
          >
            <Trash2 />
          </Button>
        </div>
      </div>

      <Modal
        centered
        open={showPreviewModal}
        width={800}
        footer={null}
        onCancel={(e) => {
          e.stopPropagation();
          toggleShowPreviewModal((prev) => !prev);
        }}
        styles={{
          body: {
            display: "flex",
            justifyContent: "center",
          },
        }}
      >
        <div className="flex flex-col w-[90%]">
          <div className="mb-6">
            <Button
              variant="primary"
              variantColor={"secondary"}
              className="float-right"
              onClick={() => {
                const link = window.document.createElement("a");
                link.href = url;
                link.download = name;
                link.target = "_blank";

                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
              }}
            >
              <ArrowDownToLine />
              Download
            </Button>
          </div>
          {isPdf ? (
            <iframe src={url} className="grow h-[80vh]" />
          ) : (
            <img src={url} className="grow" alt={name} />
          )}
        </div>
      </Modal>
    </div>
  );
};

const IconType: Record<string, typeof PdfIcon> = {
  pdf: PdfIcon,
  jpg: ImageIcon,
  png: ImageIcon,
};

export const InlinePreview = ({ document, onDelete }: InlinePreviewProps) => {
  const [open, setOpen] = useState(false);

  if (!document) return null;
  const { signedUrl, imageUrl, name, s3FileKey } = document;
  const url = signedUrl || imageUrl;
  if (!url) return null;
  const extension = getFileType(name);

  const isPdf = extension === "pdf";

  const Icon = IconType[extension || "pdf"] || PdfIcon;

  return (
    <div className="h-[64px] rounded-sm bg-[#F5F9FC] py-4 px-2 flex justify-between items-center">
      <div className="flex items-center">
        <div className="w-8 h-8 p-2 flex justify-center items-center rounded-full bg-[#EFF6FF] mr-2">
          <Icon />
        </div>
        <div>
          <div className="text-base font-semibold">{name}</div>
          <span className="text-[#6B7280] text-sm">
            {bytesToMB(document.sizeInBytes)} MB
          </span>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          variant={"ghost"}
          variantColor={"secondary"}
          size={"icon"}
          onClick={() => setOpen(true)}
        >
          <EyeIcon />
        </Button>
        <Button
          variant={"ghost"}
          variantColor={"destructive"}
          size={"icon"}
          onClick={(e) => {
            e.stopPropagation();
            apiService
              .delete(`/customers/document/`, {
                s3FileKey: s3FileKey,documentCategory: document.documentCategory,
              })
              .then((response) => {
                // Store the API response in a variable
                const apiResponse = response;

                // Show success toast
                toast.info(
                  (apiResponse as any).message ||
                    "Document deleted successfully"
                );

                // Call the onDelete callback with the response if needed
                onDelete();
              })
              .catch((error) => {
                // Show error toast
                toast.error(error.message || "Unknown error");

                // Log the error for debugging
                console.error("Document deletion error:", error);
              });
          }}
        >
          <Trash2 />
        </Button>
      </div>
      <Modal
        centered
        open={open}
        width={800}
        footer={null}
        onCancel={() => setOpen(false)}
        styles={{
          body: {
            display: "flex",
            justifyContent: "center",
          },
        }}
      >
        <div className="flex flex-col w-[90%]">
          <div className="mb-6">
            <Button
              variant="primary"
              variantColor={"secondary"}
              className="float-right"
              onClick={() => {
                const link = window.document.createElement("a");
                link.href = url;
                link.download = name;
                link.target = "_blank";

                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
              }}
            >
              <ArrowDownToLine />
              Download
            </Button>
          </div>
          {isPdf ? (
            <iframe src={url} className="grow h-[80vh]" />
          ) : (
            <img src={url} className="grow" alt={name} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export const InlinePreviewList = ({
  documentList = [],
  onDelete,
}: InlinePreviewListProps) => {
  return (
    <div className="w-full flex flex-col gap-6 overflow-auto">
      {documentList.map((doc) => (
        <InlinePreview key={doc.name} document={doc} onDelete={onDelete} />
      ))}
    </div>
  );
};

const getAllDocumentsOfParticularType = (
  documents: Document[],
  type: string
): Document[] => {
  return documents.filter((doc) => doc.documentType === type);
};

export const UploadMultipleFiles = ({
  onDrop,
  documents,
  documentType,
  onDelete,
}: UploadMultipleFilesProps) => {
  return (
    <div className="flex gap-8 h-[180px]">
      <DragAndDropUpload
        className="h-full w-[190px] min-w-[190px] border-2 border-dashed border-[#000000] shadow-none"
        onDrop={(acceptedFiles) => onDrop({ acceptedFiles, documentType })}
      >
        <DragAndDropIcon className="mb-2 h-23 w-19 mt-2 p-1" />
        <DragAndDropTitle className="mb-2 text-l">
          Drag and Drop files
        </DragAndDropTitle>
        <DragAndDropHelpText className="mb-2 text-base">or</DragAndDropHelpText>
        
        
        <Button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 mb-3" >
          Browse
        </Button>
        
      </DragAndDropUpload>
      <InlinePreviewList
        documentList={getAllDocumentsOfParticularType(documents, documentType)}
        onDelete={onDelete}
      />
    </div>
  );
};
